<script setup lang="ts">
import type { Component } from "vue";
import { computed, ref } from "vue";

import { getPropertyDisplayTitle, getPropertyPartialTask, getPropertyValueFromTask } from "~/common/properties";
import Button from "~/components/dumb/Button.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import PageIcon from "~/components/dumb/PageIcon.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { makeDropdownItem } from "~/constants/dropdown";
import { colorsByTheme } from "~/constants/style";
import { TaskKindFieldIcon } from "~/icons";
import { ButtonStyle, EditorMode, IconSize, Placement } from "~/shared/enums";
import type { DropdownMenuItem, PropertyDefaultKind, PropertyValueForKind, Task } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore } from "~/stores";

type Value = PropertyValueForKind<PropertyDefaultKind>;

const props = defineProps<{
  property: PropertyDefaultKind;
  tasks: Task[];
  defaultValue?: Value;
  editorMode: EditorMode;
  value?: Value;
  hideUnset?: boolean;
  onAfterClose?: (value: Value | null) => void;
}>();

const emit = defineEmits<{
  update: [value: Value];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();

const dropdownMenu = ref<InstanceType<typeof DropdownMenu> | null>(null);

const colors = computed(() => colorsByTheme[pageStore.theme]);

const isListMode = computed(() => props.editorMode === EditorMode.LIST);
const isChipMode = computed(() => props.editorMode === EditorMode.CHIP);
const isBoardMode = computed(() => props.editorMode === EditorMode.BOARD);
const isTaskDetailMode = computed(() => props.editorMode === EditorMode.DETAIL);
const isContextMenuMode = computed(() => props.editorMode === EditorMode.CONTEXT_MENU);
const isFormMode = computed(() => props.editorMode === EditorMode.FORM);
const isAutomationMode = computed(() => props.editorMode === EditorMode.AUTOMATION);
const isFormOrAutomationMode = computed(() => isFormMode.value || isAutomationMode.value);

const selectedTaskKindDuid = computed(() => {
  if (isFormOrAutomationMode.value) {
    return props.value ?? dataStore.taskKindList[0]?.duid ?? null;
  }

  const firstValue = getPropertyValueFromTask(props.property, props.tasks[0]);
  return props.tasks.every((task) => getPropertyValueFromTask(props.property, task) === firstValue) ? firstValue : null;
});
const selectedTaskKind = computed(() =>
  selectedTaskKindDuid.value ? dataStore.getTaskKindByDuid(selectedTaskKindDuid.value) : undefined
);

const selectedTasks = computed(() =>
  !isListMode.value
    ? props.tasks
    : dataStore.getTasksByDuidsOrderedIfLoaded([...appStore.selectedTaskDuids], {
        includeTrashed: false,
        includeDraft: false,
      })
);

const updateSelectedTaskKind = (kindDuid: Value) =>
  dataStore.updateTasks(
    selectedTasks.value.map((task) => ({ duid: task.duid, ...getPropertyPartialTask(props.property, task, kindDuid) }))
  );

const dropdownSections = computed(() => {
  const firstValue = isFormOrAutomationMode.value
    ? props.value
    : getPropertyValueFromTask(props.property, props.tasks[0]);
  const kindDuidOfSelectedTasks = isFormOrAutomationMode.value
    ? (props.value ?? dataStore.taskKindList[0]?.duid)
    : selectedTasks.value.every((task) => getPropertyValueFromTask(props.property, task) === firstValue)
      ? firstValue
      : undefined;

  const makeItem = (duid: string, title: string, icon: Component, iconArgs: object, onClick: () => void) =>
    makeDropdownItem(kindDuidOfSelectedTasks === duid, pageStore.isMobile, { title, icon, iconArgs, onClick });

  const items: DropdownMenuItem[] = [];

  items.push(
    ...dataStore.taskKindList.map((kind) =>
      makeItem(kind.duid, kind.title, PageIcon, { page: kind, size: IconSize.S }, () => {
        if (!isFormOrAutomationMode.value) {
          updateSelectedTaskKind(kind.duid);
        }
        emit("update", kind.duid);
      })
    )
  );

  return [{ title: getPropertyDisplayTitle(props.property), items }];
});

const handleAfterClose = () => {
  props.onAfterClose?.(selectedTaskKindDuid.value);
};

defineExpose({
  open: () => dropdownMenu.value?.open(),
  isOpen: computed(() => dropdownMenu.value?.isOpen),
});
</script>

<template>
  <DropdownMenu
    ref="dropdownMenu"
    :container="isChipMode ? '#dart-task-creation-modal-wrapper' : isFormMode ? '#dart-form-wrapper' : undefined"
    :sections="dropdownSections"
    header-title="Set type"
    mobile-include-done
    :block="isListMode || isTaskDetailMode || isFormOrAutomationMode"
    :height-block="isListMode || isFormOrAutomationMode"
    :distance="isFormOrAutomationMode ? 2 : undefined"
    :skidding="isFormOrAutomationMode ? -1 : undefined"
    :cover="!isContextMenuMode && !isFormOrAutomationMode"
    :placement="isContextMenuMode ? Placement.RIGHT_TOP : Placement.BOTTOM_LEFT"
    :style="{ '--background': colors.borderVlt, '--highlight': colors.borderMd }"
    :on-after-close="handleAfterClose">
    <DropdownMenuItemContent
      v-if="isContextMenuMode"
      :icon="TaskKindFieldIcon"
      :title="`Change ${getPropertyDisplayTitle(property).toLowerCase()}`"
      is-submenu />
    <Tooltip
      v-else
      :disabled="isFormOrAutomationMode"
      :class="{ 'overflow-hidden': isFormOrAutomationMode }"
      :text="
        isBoardMode ? getPropertyDisplayTitle(property) : `Change ${getPropertyDisplayTitle(property).toLowerCase()}`
      "
      :block="isListMode || isTaskDetailMode || isFormOrAutomationMode"
      :height-block="isListMode || isFormOrAutomationMode">
      <div
        v-if="isTaskDetailMode"
        class="flex w-full items-center justify-start gap-2 rounded-sm py-1 pr-2 pl-1.5 text-left hover:bg-lt">
        <div v-if="!isListMode && !selectedTaskKind" class="text-vlt">
          {{ `Set ${getPropertyDisplayTitle(property).toLowerCase()}` }}
        </div>
        <template v-else>
          <component
            :is="selectedTaskKind ? PageIcon : TaskKindFieldIcon"
            :page="selectedTaskKind"
            :size="selectedTaskKind && isBoardMode ? IconSize.S : undefined"
            :class="!selectedTaskKind && 'icon-md'" />
          <span :title="selectedTaskKind?.title" class="flex-1 truncate select-none">
            {{ selectedTaskKind?.title }}
          </span>
        </template>
      </div>
      <Button
        v-else
        :btn-style="ButtonStyle.CHIP"
        :icon="selectedTaskKind ? PageIcon : isFormOrAutomationMode ? undefined : TaskKindFieldIcon"
        :icon-args="
          selectedTaskKind ? { page: selectedTaskKind, size: isBoardMode ? IconSize.S : undefined } : undefined
        "
        :icon-size="isBoardMode ? IconSize.XS : undefined"
        block
        :text="!selectedTaskKind && isFormOrAutomationMode ? 'Set type' : selectedTaskKind?.title"
        :disable-hover="isListMode || isFormOrAutomationMode || isBoardMode"
        :borderless="!isChipMode && !isBoardMode"
        :text-style="isFormOrAutomationMode && !selectedTaskKind ? 'text-vlt' : 'text-md'"
        :class="{
          '!rounded': !isListMode,
          'gap-2': !isChipMode && !isBoardMode,
          'px-1 py-0.5': !isListMode && !isFormOrAutomationMode && !isBoardMode,
          '!justify-start px-[7px]': isFormOrAutomationMode || isListMode,
          'max-w-xs truncate': isChipMode,
          'text-xs': isListMode || isBoardMode,
          'h-5 gap-1 px-1 py-0': isBoardMode,
        }"
        a11y-label="Type" />
    </Tooltip>
  </DropdownMenu>
</template>
